// sourceService.js - Version épurée compatible avec le backend optimisé + SUPPORT IMAGES
import axios from 'axios';

/**
 * Service simplifié pour la gestion des sources avec vérification d'accessibilité optimisée
 * ✅ SUPPORT IMAGES : Normalisation automatique des images provenant du backend
 */
const sourceService = {
  /**
   * MÉTHODE PRINCIPALE: Recommande des sources avec vérification d'accessibilité optimale
   * ✅ SUPPORT IMAGES : Les sources retournées incluent imageUrl extraite des métadonnées d'images
   * @param {Object} params - Paramètres de recherche
   * @param {string} token - Token d'authentification
   * @returns {Promise<Object>} - Résultat avec sources accessibles et métadonnées enrichies
   */
  async getRecommendedSources(params, token) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      const {
        userId,
        competenceId,
        userObjective,
        query,
        languages = ['en'],      
        country = '',              
        maxSources = 20,
        selectedProviders = [],
        preferredSourceTypes = null,
        useAI = true,
        // OPTIONS D'ACCESSIBILITÉ OPTIMALES
        verifyAccessibility= true,
        enableDomainFallback= true,
        detectParking= true,
        accessibilityMode= 'enhanced',
        // OPTIONS DE TRADUCTION
        translateToTargetLanguage = true,
        forceTranslation = false,
  
      } = params;

      if (!userId || !userObjective) {
        throw new Error('Les paramètres userId et userObjective sont requis');
      }

      console.log("🔍 Service - Requête avec accessibilité optimisée:", {
        verifyAccessibility,
        providers: selectedProviders.length
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/recommend`,
        {
          userId,
          competenceId,
          userObjective,
          query,
          languages,
          country,
          maxSources,
          selectedProviders,
          preferredSourceTypes,
          useAI,
          // OPTIONS D'ACCESSIBILITÉ
          verifyAccessibility,
          enableDomainFallback,
          detectParking,
          accessibilityMode,  
          // OPTIONS DE TRADUCTION
          translateToTargetLanguage,
          forceTranslation,
          // OPTIONS DE LOCALISATION GÉOGRAPHIQUE
          enableGeographicLocalization: params.enableGeographicLocalization,
          localGlobalRatio: params.localGlobalRatio,
          geographicAnalysis: params.geographicAnalysis,
          userPreferences: params.userPreferences
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // VALIDATION ET NORMALISATION DES SOURCES REÇUES
      if (response.data && response.data.sources) {
        const validatedSources = response.data.sources.filter(source => {
          const hasValidUrl = !!(source.recommendedUrl || source.usableUrl || source.link || source.url || source.domain);
          if (!hasValidUrl) {
            console.warn('Source sans URL valide détectée et filtrée:', source);
          }
          return hasValidUrl;
        });

        if (validatedSources.length !== response.data.sources.length) {
          console.warn(`${response.data.sources.length - validatedSources.length} sources sans URL valide filtrées`);
        }

        // ✅ NORMALISER TOUTES LES SOURCES AVEC URLs RECOMMANDÉES + IMAGES
        const normalizedSources = validatedSources.map(source => this.normalizeSourceWithRecommendedUrl(source));

        // 🖼️ Log pour debug images
        const sourcesWithImages = normalizedSources.filter(source => source.imageUrl && !source.imageUrl.includes('favicon'));
        if (sourcesWithImages.length > 0) {
          console.log(`🖼️ ${sourcesWithImages.length}/${normalizedSources.length} sources avec images de qualité`);
        }

        console.log("getRecommendedSources", normalizedSources);

        return {
          ...response.data,
          sources: normalizedSources
        };
      }
      
      return response.data;

    } catch (error) {
      console.error('Erreur lors de la récupération des sources recommandées:', error);
      throw error;
    }
  },

  /**
   * ✅ AMÉLIORATION: Normalisation des sources avec URLs optimisées + EXTRACTION IMAGES
   * @param {Object} source - Source à normaliser
   * @returns {Object} - Source normalisée avec URL utilisable et imageUrl
   */
  normalizeSourceWithRecommendedUrl(source) {
    // ✅ UTILISER usableUrl ou recommendedUrl en priorité (optimisées par le backend)
    const bestUrl = source.usableUrl || source.recommendedUrl || source.link || source.url;
    
    // 🖼️ NOUVEAU : Extraction intelligente de la meilleure image
    const imageUrl = this.extractBestImageUrl(source);
    
    return {
      ...source,
      // URLs utilisables (ordre de priorité)
      usableUrl: bestUrl,
      recommendedUrl: bestUrl,
      originalUrl: source.originalUrl || source.url || source.link,
      
      // 🖼️ NOUVEAU : Image optimale extraite
      imageUrl: imageUrl,
      
      // Informations d'accessibilité (si disponibles)
      hasUrlFallback: !!source.fallbackApplied,
      accessibilityStatus: source.accessibilityStatus || 'verified',
      verificationMethod: source.verificationMethod || 'unknown',
      
      // Provider information (nouveau)
      provider: source.provider || 'unknown',
      searchProvider: source.searchProvider || source.provider,
      
      // 🖼️ Métadonnées images (pour debug/info)
      imageMetadata: source.images ? {
        hasMain: !!source.images.main,
        hasThumbnail: !!source.images.thumbnail,
        hasFallback: !!source.images.fallback,
        selectedSource: this.getSelectedImageSource(source.images)
      } : null
    };
  },

  /**
   * 🖼️ NOUVELLE MÉTHODE : Extraction intelligente de la meilleure image
   * @param {Object} source - Source avec potentielles images
   * @returns {string|null} - URL de la meilleure image disponible
   */
  extractBestImageUrl(source) {
    // Si la source a déjà une imageUrl, la conserver
    if (source.imageUrl && !source.imageUrl.includes('s2/favicons')) {
      return source.imageUrl;
    }

    // Extraire depuis les métadonnées d'images du backend
    if (source.images) {
      const { main, thumbnail, fallback } = source.images;
      
      // Hiérarchie de qualité : main > thumbnail > fallback (mais pas favicon)
      if (main?.url && !main.url.includes('s2/favicons')) {
        return main.url;
      }
      
      if (thumbnail?.url && !thumbnail.url.includes('s2/favicons')) {
        return thumbnail.url;
      }
      
      // Accepter le fallback seulement si c'est pas un favicon trop petit
      if (fallback?.url && !fallback.url.includes('s2/favicons')) {
        return fallback.url;
      }
    }

    // Fallback ultime : essayer de générer une image thématique
    return this.generateFallbackImage(source);
  },

  /**
   * 🖼️ NOUVELLE MÉTHODE : Génération d'image de fallback thématique
   * @param {Object} source - Source pour laquelle générer une image
   * @returns {string} - URL d'image de fallback
   */
  generateFallbackImage(source) {
    const domain = source.domain || source.provider || '';
    const title = source.title || '';
    const description = source.description || '';
    
    // Détecter le thème basé sur le contenu
    const content = `${domain} ${title} ${description}`.toLowerCase();
    
    const themeKeywords = {
      technology: ['tech', 'software', 'ai', 'digital', 'computer', 'app'],
      business: ['business', 'startup', 'entrepreneur', 'company', 'corporate'],
      education: ['learn', 'education', 'course', 'university', 'tutorial'],
      news: ['news', 'article', 'journal', 'media', 'press'],
      finance: ['finance', 'money', 'investment', 'bank', 'economic'],
      science: ['science', 'research', 'study', 'discovery', 'experiment']
    };
    
    // Trouver le thème le plus pertinent
    let detectedTheme = 'business'; // défaut
    let maxMatches = 0;
    
    for (const [theme, keywords] of Object.entries(themeKeywords)) {
      const matches = keywords.filter(keyword => content.includes(keyword)).length;
      if (matches > maxMatches) {
        maxMatches = matches;
        detectedTheme = theme;
      }
    }
    
    // Générer une URL Unsplash basée sur le thème et le domaine
    const searchTerm = `${detectedTheme} ${domain}`.replace(/[^a-zA-Z0-9 ]/g, '').trim();
    return `https://source.unsplash.com/400x240/?${encodeURIComponent(searchTerm)}`;
  },

  /**
   * 🖼️ NOUVELLE MÉTHODE : Déterminer la source d'image sélectionnée
   * @param {Object} images - Objet images du backend
   * @returns {string} - Source de l'image sélectionnée
   */
  getSelectedImageSource(images) {
    if (images.main?.url && !images.main.url.includes('s2/favicons')) {
      return images.main.source || 'main';
    }
    if (images.thumbnail?.url && !images.thumbnail.url.includes('s2/favicons')) {
      return images.thumbnail.source || 'thumbnail';
    }
    if (images.fallback?.url) {
      return images.fallback.source || 'fallback';
    }
    return 'none';
  },

  /**
   * ✅ AMÉLIORATION: Vérification d'accessibilité avec fallback intelligent
   * @param {string} url - URL à vérifier
   * @param {string} token - Token d'authentification
   * @param {Object} options - Options de vérification
   * @returns {Promise<Object>} - Résultat enrichi de la vérification
   */
  async checkUrlAccessibility(url, token, options = {}) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }
      
      const {
        enableDomainFallback = true,
        detectParking = true,
        testType = 'enhanced'
      } = options;
      
      // S'assurer que l'URL est bien formatée
      const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/check-url`,
        { 
          url: formattedUrl,
          enableDomainFallback,
          detectParking,
          testType
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'URL:', error);
      return {
        success: false,
        accessible: false,
        error: error.message || 'Erreur lors de la vérification'
      };
    }
  },

  /**
   * ✅ NOUVEAU: Vérification en lot d'URLs
   * @param {Array} urls - URLs à vérifier
   * @param {string} token - Token d'authentification
   * @param {Object} options - Options de vérification
   * @returns {Promise<Object>} - Résultats de vérification en lot
   */
  async batchCheckAccessibility(urls, token, options = {}) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }
      
      if (!urls || !Array.isArray(urls) || urls.length === 0) {
        throw new Error('Array d\'URLs requis');
      }
      
      const {
        enableDomainFallback = true,
        detectParking = true,
        maxConcurrent = 3
      } = options;
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/check-url`,
        { 
          urls,  // ← Envoyer l'array d'URLs
          enableDomainFallback,
          detectParking,
          maxConcurrent
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la vérification en lot:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la vérification en lot'
      };
    }
  },

  /**
   * ✅ OPTIONNEL: Statistiques d'accessibilité (pour monitoring)
   * @param {string} token - Token d'authentification
   * @returns {Promise<Object>} - Statistiques du système
   */
  async getAccessibilityStats(token) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/accessibility-stats`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw error;
    }
  },

  // ========================================== 
  // MÉTHODES EXISTANTES CONSERVÉES (inchangées)
  // ==========================================

  /**
   * Obtient les métadonnées des fournisseurs disponibles
   */
  async getProvidersMetadata(token) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/providers`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data.metadata;
    } catch (error) {
      console.error('Erreur lors de la récupération des métadonnées des fournisseurs:', error);
      throw error;
    }
  },

  /**
   * Récupère les sources sauvegardées d'un utilisateur
   */
  async getSavedSources(userId, token, filters = {}) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/user/${userId}`,
        {
          params: filters,
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des sources sauvegardées:', error);
      throw error;
    }
  },

  /**
   * Sauvegarde des sources pour un utilisateur
   */
  async saveSources(params, token) {
    console.log("Envoi des données à l'API pour sauvegarde");
    console.log("Appel de SourceService.saveSources(param, token) - param =", params);

    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      const {
        userId,
        competenceId,
        userObjective,
        query,
        sources,
        category,
        providers
      } = params;

      if (!userId || !sources || !Array.isArray(sources) || sources.length === 0) {
        throw new Error('Les paramètres userId et sources (array non vide) sont requis');
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/save`,
        {
          userId,
          competenceId,
          userObjective,
          query,
          sources,
          category,
          providers
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des sources:', error);
      throw error;
    }
  },

  /**
   * Met à jour des sources existantes
   */
  async updateSources(sessionId, updatedSources, category, token, competenceId) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }
      
      const updateData = {
        sources: updatedSources,
        competenceId: competenceId
      };
      
      if (category) {
        updateData.category = category;
      }
      
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/update/${sessionId}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des sources:', error);
      throw error;
    }
  },

  /**
   * Supprime un enregistrement de sources
   */
  async deleteSources(sourceId, token, params = {}) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/delete/${sourceId}`,
        {
          data: params,
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression des sources:', error);
      throw error;
    }
  },

  /**
   * Recherche des sources par mots-clés
   */
  async searchSources(userId, searchParams, token) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/search`,
        {
          params: {
            userId,
            ...searchParams
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la recherche de sources:', error);
      throw error;
    }
  },

  /**
   * Obtient les statistiques d'utilisation des sources
   */
  async getSourcesStats(userId, token, period = 'month') {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/stats/${userId}`,
        {
          params: { period },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw error;
    }
  },
  
  /**
   * Recommande des fournisseurs pour une requête
   */
  async getRecommendedProviders(query, userObjective, token) {
    try {
      console.log("Service: Demande de recommandations avec:", { query, userObjective });
      
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }
      
      if (!userObjective || userObjective.trim() === '') {
        console.warn("Service: Objectif vide dans la demande de recommandations");
        return [];
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/recommend-providers`,
        {
          query,
          userObjective
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log("Service: Réponse de l'API:", response.data);
      
      // Extraire uniquement les noms des providers de la réponse
      if (response.data && response.data.providers && Array.isArray(response.data.providers)) {
        return response.data.providers.map(provider => provider.name);
      }
      
      return [];
    } catch (error) {
      console.error('Erreur lors de la récupération des fournisseurs recommandés:', error);
      return [];
    }
  },

  // FONCTIONS DE TRADUCTION (conservées du code existant)
  async getTranslationStats(token) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/translation-stats`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques de traduction:', error);
      throw error;
    }
  },

  async testTranslation(query, targetCountry, userLanguages, token) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/test-translation`,
        {
          query,
          targetCountry,
          userLanguages
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Erreur lors du test de traduction:', error);
      throw error;
    }
  },

  async clearTranslationCaches(token) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/clear-translation-caches`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Erreur lors du nettoyage des caches de traduction:', error);
      throw error;
    }
  },

  async configureTranslation(translationOptions, token) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/configure-translation`,
        {
          translationOptions
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la configuration de traduction:', error);
      throw error;
    }
  },

  async getTranslationReport(token) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/translation-report`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du rapport de traduction:', error);
      throw error;
    }
  },

    /**
   * Supprime une source spécifique d'un document de recherche existant
   * @param {string} userId - ID de l'utilisateur
   * @param {string} sessionId - ID de la session/recherche
   * @param {Object} sourceToDelete - Source à supprimer
   * @param {string} token - Token d'authentification
   * @param {string} competenceId - ID de la compétence (optionnel)
   * @returns {Promise<Object>} - Résultat de la suppression
   */
    async removeSourceFromSearchDoc(userId, sessionId, sourceToDelete, token, competenceId) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      if (!userId || !sessionId || !sourceToDelete) {
        throw new Error('Paramètres manquants: userId, sessionId et sourceToDelete sont requis');
      }

      console.log('🗑️ Suppression de source:', {
        userId,
        sessionId,
        sourceTitle: sourceToDelete.title || sourceToDelete.domain,
        competenceId
      });

      // ✅ CORRECTION : Utiliser la route DELETE existante
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/delete/${sessionId}`,
        {
          data: {
            sourceUrl: sourceToDelete.link,
            competenceId: competenceId
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      console.error('❌ Erreur lors de la suppression de la source:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de la suppression de la source'
      };
    }
   },

    /**
   * Ajoute une source manuellement à un document de recherche existant
   * @param {string} userId - ID de l'utilisateur
   * @param {string} sessionId - ID de la session/recherche
   * @param {Object} newSource - Nouvelle source à ajouter
   * @param {string} token - Token d'authentification
   * @param {string} competenceId - ID de la compétence (optionnel)
   * @returns {Promise<Object>} - Résultat de l'ajout
   */
  async addSourceToSearchDoc(userId, sessionId, newSource, token, competenceId) {
    try {
      if (!token) {
        throw new Error('Token d\'authentification requis');
      }

      if (!userId || !sessionId || !newSource) {
        throw new Error('Paramètres manquants: userId, sessionId et newSource sont requis');
      }

      console.log('➕ Ajout de source manuelle:', {
        userId,
        sessionId,
        sourceTitle: newSource.title || newSource.domain,
        competenceId
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/sources/add-source`,
        {
          userId,
          sessionId,
          newSource,
          competenceId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout de la source:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de l\'ajout de la source'
      };
    }
  }

};

export default sourceService;
